/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useContext } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { useIntl } from 'react-intl-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ADVANCED_SEARCH_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import type {
	useFilterSupplierAdvancedSearchFilterQuery as useFilterSupplierAdvancedSearchFilterQuery$data,
	useFilterSupplierAdvancedSearchFilterQueryVariables as useFilterSupplierAdvancedSearchFilterQuery$variables,
} from './__types__/useFilterSupplierAdvancedSearchFilterQuery.ts';
import { FilterIds, OptionIds, defaultFilterState } from './AdvancedSearch-constants.ts';
import { getOptionsToHydrate, mapDataToOptions, areFiltersConsistent } from './utils/filters.tsx';
import type { FilterOption, FilterStateType, ParsedUrlQuery } from './AdvancedSearch-types.ts';
import { ActivitiesObjectType, ActivityProduct } from './gql/__types__/AdvancedSearchFilterQuery.ts';
import { i18n } from './AdvancedSearch-i18n.ts';

type filterSupplierArgs = {
	queryParams: ParsedUrlQuery;
	filters: FilterStateType;
	setFilters: any;
	setUpdatedQueryParams: any;
};

type FilterSupplierType = {
	loading: boolean;
	spaceOptions: FilterOption[];
	contributorOptions: FilterOption[];
	typeOptions: FilterOption[];
	ancestorOptions: FilterOption[];
};

export function useFilterSupplier({
	filters,
	queryParams,
	setFilters,
	setUpdatedQueryParams,
}: filterSupplierArgs): FilterSupplierType {
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardsEnabledAS = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isDatabasesEnabledAS = isContentTypeEnabledInCurrentEnv('database');
	const { formatMessage } = useIntl();
	const { userId, cloudId } = useSessionData();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const {
		contributorsToHydrate,
		spacesToHydrate,
		contributors,
		spaces,
		lastModified,
		ancestorId,
		selectedType,
	} = getOptionsToHydrate(queryParams, filters);
	const filtersAreInParity = areFiltersConsistent(filters, [
		...contributors,
		...spaces,
		...(lastModified ? [lastModified] : []),
	]);
	const typesRequireHydration = filters[FilterIds.TYPE].length <= 1;
	const ancestorRequiresHydration =
		ancestorId !== '' &&
		filters[FilterIds.ANCESTOR].some((ancestorOption) =>
			Boolean(ancestorOption.isSelected && ancestorOption.value !== ancestorId),
		);

	const shouldSkipHydration = Boolean(
		contributorsToHydrate.length + spacesToHydrate.length === 0 &&
			!typesRequireHydration &&
			!ancestorRequiresHydration,
	);

	const objectTypes = [ActivitiesObjectType.PAGE, ActivitiesObjectType.EMBED];
	if (isWhiteboardsEnabledAS) {
		objectTypes.push(ActivitiesObjectType.WHITEBOARD);
	}
	if (isDatabasesEnabledAS) {
		objectTypes.push(ActivitiesObjectType.DATABASE);
	}

	const { data, loading, error } = useQuery<
		useFilterSupplierAdvancedSearchFilterQuery$data,
		useFilterSupplierAdvancedSearchFilterQuery$variables
	>(
		graphql`
			query useFilterSupplierAdvancedSearchFilterQuery(
				$userId: String!
				$isAnon: Boolean!
				$accountIds: [String]
				$spaceKeys: [String]
				$ancestorId: ID
				$hasUsers: Boolean!
				$hasSpaces: Boolean!
				$hasAncestor: Boolean!
				$filter: ActivitiesArguments!
				$first: Int
			) {
				getSmartFeatures(
					input: [
						{
							entityType: "user"
							entityIds: [$userId]
							features: ["recommendedSpaces", "recommendedPeople"]
						}
					]
				) @skip(if: $isAnon) {
					results {
						entityType
						... on SmartFeaturesUserResultResponse {
							entityType
							result {
								id
								features {
									recommendedPeople {
										score
										user {
											id
											displayName
											confluence {
												accountId
											}
											photos {
												value
											}
										}
									}
									recommendedSpaces {
										score
										space {
											id
											key
											name
											icon {
												path(type: ABSOLUTE)
											}
										}
									}
								}
							}
						}
					}
				}
				spaces(spaceKeys: $spaceKeys) @include(if: $hasSpaces) {
					nodes {
						id
						key
						name
						icon {
							path(type: ABSOLUTE)
						}
					}
				}
				confluenceUsers(accountIds: $accountIds) @include(if: $hasUsers) {
					nodes {
						displayName
						profilePicture {
							path
						}
						... on KnownUser {
							accountId
						}
						... on UnknownUser {
							accountId
						}
					}
				}
				contentTypes: cqlMetaData {
					cqlContentTypes {
						type
						label
					}
				}
				activities @skip(if: $isAnon) {
					myActivities {
						viewed(filters: [{ type: AND, arguments: $filter }], first: $first) {
							edges {
								cursor
								node {
									id
									object {
										id
										content {
											id
											title
											type
										}
									}
								}
							}
							pageInfo {
								hasNextPage
							}
						}
					}
				}
				ancestorData: singleContent(id: $ancestorId) @include(if: $hasAncestor) {
					id
					title
					type
				}
				currentUser: confluence_atlassianUser(current: true) @skip(if: $isAnon) {
					id
					displayName
					photos {
						value
						isPrimary
					}
					confluence {
						accountId
					}
				}
			}
		`,
		{
			variables: {
				userId: userId || '',
				isAnon: !userId,
				accountIds: contributorsToHydrate,
				spaceKeys: spacesToHydrate,
				ancestorId,
				hasUsers: userId ? contributorsToHydrate.length > 0 : false,
				hasSpaces: spacesToHydrate.length > 0,
				hasAncestor: ancestorId !== '',
				filter: {
					products: [ActivityProduct.CONFLUENCE],
					objectTypes,
					cloudIds: [cloudId],
				},
				first: 10,
			},
			skip: shouldSkipHydration,
			errorPolicy: 'all',
			onCompleted: () => {
				if (!shouldSkipHydration) {
					const updatedFilters = { ...filters };
					updatedFilters[FilterIds.SPACES] = [...spaceOptions];
					updatedFilters[FilterIds.CONTRIBUTORS] = [...contributorOptions];
					updatedFilters[FilterIds.TYPE] = [...typeOptions];
					updatedFilters[FilterIds.ANCESTOR] = [...ancestorOptions];
					setFilters(updatedFilters);
					if (!filtersAreInParity) {
						setUpdatedQueryParams(updatedFilters);
					}
				}

				if (shouldSkipHydration && !filtersAreInParity) {
					const updatedFilters = { ...defaultFilterState };
					updatedFilters[FilterIds.DATE][OptionIds.LAST_MODIFIED] = [
						...filters[FilterIds.DATE][OptionIds.LAST_MODIFIED].map((option) => ({
							...option,
							isSelected: lastModified === option.value,
						})),
					];
					updatedFilters[FilterIds.SPACES] = [
						...filters[FilterIds.SPACES].map((option) => ({
							...option,
							isSelected: !!spaces.includes(option.value),
						})),
					];
					updatedFilters[FilterIds.CONTRIBUTORS] = [
						...filters[FilterIds.CONTRIBUTORS].map((option) => ({
							...option,
							isSelected: !!contributors.includes(option.value),
						})),
					];
					setFilters(updatedFilters);
				}
			},
		},
	);

	if (error) {
		if (isExpectedApolloError(error)) {
			markErrorAsHandled(error);
			experienceTracker.abort({
				name: ADVANCED_SEARCH_EXPERIENCE,
				reason: 'Expected GQL error',
			});
		} else {
			experienceTracker.stopOnError({
				name: ADVANCED_SEARCH_EXPERIENCE,
				error,
			});
		}
	}

	const defaultTypesFilterOptions: FilterOption[] = [
		{
			value: 'page',
			label: formatMessage(i18n.typePage),
			isSelected: 'page' === selectedType,
		},
		{
			value: 'blogpost',
			label: formatMessage(i18n.typeBlogpost),
			isSelected: 'blogpost' === selectedType,
		},
		{
			value: 'attachment',
			label: formatMessage(i18n.typeAttachment),
			isSelected: 'attachment' === selectedType,
		},
		{
			value: 'space',
			label: formatMessage(i18n.typeSpace),
			isSelected: 'space' === selectedType,
		},
		{
			value: 'comment',
			label: formatMessage(i18n.typeComments),
			isSelected: 'comment' === selectedType,
		},
		{
			value: 'embed',
			label: formatMessage(i18n.typeEmbed),
			isSelected: 'embed' === selectedType,
		},
	]
		.concat(
			isWhiteboardsEnabledAS
				? [
						{
							value: 'whiteboard',
							label: formatMessage(i18n.typeWhiteboard),
							isSelected: 'whiteboard' === selectedType,
						},
					]
				: [],
		)
		.concat(
			isDatabasesEnabledAS
				? [
						{
							value: 'database',
							label: formatMessage(i18n.typeDatabase),
							isSelected: 'database' === selectedType,
						},
					]
				: [],
		)
		.concat([
			{
				value: 'folder',
				label: formatMessage(i18n.typeFolder),
				isSelected: 'folder' === selectedType,
			},
		]);

	const { ancestorOptions, hydratedTypes, spaceOptions, contributorOptions } = mapDataToOptions({
		data,
		spaces,
		contributors,
		contributorsToHydrate,
		spacesToHydrate,
		selectedType,
		defaultTypesFilterOptions,
		isWhiteboardsEnabledAS,
		isDatabasesEnabledAS,
		userId,
	});

	const typeOptions = [...defaultTypesFilterOptions, ...hydratedTypes];

	return {
		loading,
		spaceOptions,
		contributorOptions,
		typeOptions,
		ancestorOptions,
	};
}
